// A panel under its button (moved here from the Purchasing plan so the Make plan can use it too).
import { type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/** A panel under its button, kept on screen; closes on outside click, Esc or scroll. */
export function Popover({
  button,
  children,
  width = 380,
}: {
  button: (toggle: () => void, open: boolean) => ReactNode;
  children: (close: () => void) => ReactNode;
  width?: number;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const close = () => setRect(null);
  useLayoutEffect(() => {
    if (!rect) return setPos(null);
    const h = panel.current?.offsetHeight ?? 0;
    const below = rect.bottom + 4;
    const top = below + h > window.innerHeight - 8 ? Math.max(8, rect.top - h - 4) : below;
    setPos({ top, left: Math.min(Math.max(8, rect.right - width), window.innerWidth - width - 8) });
  }, [rect, width]);
  useEffect(() => {
    if (!rect) return;
    const onDown = (e: MouseEvent) => {
      if (!panel.current?.contains(e.target as Node) && !anchor.current?.contains(e.target as Node)) setRect(null);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setRect(null);
    const onScroll = (e: Event) => !panel.current?.contains(e.target as Node) && setRect(null);
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [rect]);
  return (
    <span ref={anchor}>
      {button(() => setRect(rect ? null : (anchor.current?.getBoundingClientRect() ?? null)), !!rect)}
      {rect &&
        createPortal(
          <div
            ref={panel}
            data-popover
            style={{ position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, width }}
            className="z-40 rounded-lg border border-line bg-surface p-3 text-left shadow-pop"
          >
            {children(close)}
          </div>,
          document.body,
        )}
    </span>
  );
}
